import { Check } from "lucide-react";
import { MIN_EXAMPLE_CHARS, missingExample, needsExample } from "../../utils/appraisal";

/**
 * Under a manager rating: a 4 or 5 opens its own box for the example.
 *
 * A 3 is a solid job and needs nothing more. A 4 means they went above what
 * the role needs, so the manager says how. A 5 is outstanding, so it needs
 * clear example(s). The same rule is checked when the review is submitted.
 */
export default function ExampleBox({
  rating,
  value,
  onChange,
  disabled,
  subject = "this",
}: {
  rating: number | null | undefined;
  value: string | null | undefined;
  onChange: (text: string) => void;
  disabled?: boolean;
  /** What is being rated, for the label ("this responsibility"). */
  subject?: string;
}) {
  if (!needsExample(rating)) return null;
  const five = rating === 5;
  const text = value ?? "";
  const len = text.trim().length;
  const missing = missingExample(rating, text);
  return (
    <div className={`mt-2 rounded-lg border p-2.5 ${missing ? "border-amber-200 bg-amber-50" : "border-green-200 bg-green-50"}`}>
      <label className="block text-xs font-semibold text-gray-800">
        {five
          ? `You rated ${subject} 5 (outstanding). Give clear examples.`
          : `You rated ${subject} 4 (above expectations). How did they go above what the role needs?`}
      </label>
      <p className="mt-0.5 text-[11px] text-gray-600">
        {five
          ? "What did they do, when, and what difference did it make? Give more than one if you can."
          : "One specific example: what they did, when, and the difference it made."}
      </p>
      <textarea
        rows={3}
        disabled={disabled}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        placeholder={five
          ? "e.g. Rebuilt the stock count process in March. Counts now take 2 days, not 5, and the other sites copied it."
          : "e.g. Ran the October payroll alone while Sam was off, with no errors."}
        className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
      />
      {missing ? (
        <p role="alert" className="mt-1 text-[11px] text-amber-800">
          {len === 0
            ? `A ${rating} needs this example before you can submit. Or change the rating to a 3 if it was a solid job.`
            : `A little more, please (${MIN_EXAMPLE_CHARS - len} more characters).`}
        </p>
      ) : (
        <p className="mt-1 flex items-center gap-1 text-[11px] text-green-700">
          <Check size={12} strokeWidth={3} aria-hidden /> Example given for the {rating}.
        </p>
      )}
    </div>
  );
}
